import React, { useState } from 'react';
import { Calendar, ArrowUpDown, Download, MoreHorizontal } from 'lucide-react';
import DatePickerModal from './common/DatePickerModal';

const TinyLineChart = ({ color = '#ef4444' }) => (
  <svg width="80" height="40" viewBox="0 0 80 40">
    <path d="M 0 20 C 10 10, 20 30, 30 20 S 50 10, 60 20 S 70 30, 80 20" stroke={color} fill="none" strokeWidth="2" />
  </svg>
);

const StatCard = ({ title, value, chartColor, iconBgColor, icon }) => (
  <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between">
    <div>
      <p className="text-gray-500 text-sm">{title}</p>
      <p className="text-2xl font-bold text-gray-800 my-1">{value}</p>
    </div>
    <div className="flex items-center gap-4">
        <TinyLineChart color={chartColor} />
        <div className={`w-10 h-10 rounded-full grid place-items-center ${iconBgColor}`}>
            {icon}
        </div>
    </div>
  </div>
);

const StatusPill = ({ active }) => (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        {active ? 'اشتراك فعال' : 'انتهى الاشتراك'}
    </span>
);

const ReportsPage = () => {
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const subscribers = [
    { name: 'سراج صالح', phone: '(818) 472-3905', country: 'مصر', consultations: 3, plan: 'محامي محترف', date: '2025/11/04', active: true },
    { name: 'طارق خلفاي', phone: '(818) 472-3905', country: 'مصر', consultations: 4, plan: 'قاضي مبتدئ', date: '2025/10/25', active: true },
    { name: 'أشرف حسن', phone: '(818) 472-3905', country: 'مصر', consultations: 6, plan: 'محامي محترف', date: '2025/09/13', active: true },
    { name: 'عبدالحسن سالم', phone: '(818) 472-3905', country: 'مصر', consultations: 8, plan: 'محامي محترف', date: '2025/07/17', active: true },
    { name: 'أحمد محرم', phone: '(818) 472-3905', country: 'مصر', consultations: 10, plan: 'محامي محترف', date: '2025/09/18', active: false },
    { name: 'يوسف داود', phone: '(818) 472-3905', country: 'مصر', consultations: 27, plan: 'محامي مبتدئ', date: '2025/08/11', active: true },
    { name: 'علي الزهري', phone: '(818) 472-3905', country: 'مصر', consultations: 38, plan: 'محامي مبتدئ', date: '2025/06/22', active: true },
    { name: 'حسن مراد', phone: '(818) 472-3905', country: 'مصر', consultations: 17, plan: 'محامي محترف', date: '2025/02/28', active: false },
    { name: 'عائشة البدوي', phone: '(818) 472-3905', country: 'مصر', consultations: 10, plan: 'محامي مبتدئ', date: '2025/01/12', active: true },
    { name: 'رامي أبو حمد', phone: '(818) 472-3905', country: 'مصر', consultations: 54, plan: 'محامي محترف', date: '2024/12/05', active: true },
    { name: 'إيمان عادل', phone: '(818) 472-3905', country: 'مصر', consultations: 24, plan: 'محامي محترف', date: '2024/11/15', active: false },
    { name: 'ليلى الجبالي', phone: '(818) 472-3905', country: 'مصر', consultations: 24, plan: 'محامي محترف', date: '2024/09/17', active: true },
    { name: 'أسامة سعيد', phone: '(818) 472-3905', country: 'مصر', consultations: 65, plan: 'محامي محترف', date: '2024/08/30', active: false },
  ];

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen" dir="rtl">
      <DatePickerModal isOpen={isDatePickerOpen} onClose={() => setDatePickerOpen(false)} />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-[#1D2035]">إدارة التقارير</h1>
        <div className="flex items-center gap-4">
            <button onClick={() => setDatePickerOpen(true)} className="flex items-center gap-2 px-4 py-2 border bg-white rounded-lg text-sm">
              <Calendar size={16} />
              <span>يناير 2025 - يونيو 2025</span>
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border bg-white rounded-lg text-sm">
              <ArrowUpDown size={16} />
              <span>من الاحدث الى الاقدم</span>
            </button>
            <button className="px-6 py-2.5 bg-[var(--Primary-Primary)] text-white rounded-lg font-semibold shadow-sm hover:bg-red-700 transition-colors flex items-center gap-2">
                <Download size={16} />
                <span>تصدير التقرير</span>
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard title="إجمالي أرباح الاشتراكات" value="$6,240" chartColor="#34d399" iconBgColor="bg-green-100" icon={<span className="text-green-600 text-xl font-bold">$</span>} />
        <StatCard title="إجمالي الاستشارات القانونية" value="724" chartColor="#fb923c" iconBgColor="bg-orange-100" icon={<span className="text-orange-600 text-xl font-bold">⚖️</span>} />
        <StatCard title="اشتراكات تم الغائها" value="270" chartColor="#f87171" iconBgColor="bg-red-100" icon={<span className="text-red-600 text-xl font-bold">!</span>} />
        <StatCard title="الاشتراكات" value="1,200" chartColor="#60a5fa" iconBgColor="bg-blue-100" icon={<span className="text-blue-600 text-xl font-bold">👥</span>} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 flex justify-between items-center">
            <h2 className="font-semibold text-lg text-gray-800">تقارير المشتركين</h2>
            <button className="text-sm text-gray-600 hover:text-gray-800">عرض الكل</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead className="bg-gray-50 text-gray-600 font-medium">
              <tr>
                <th className="p-3 w-10"><input type="checkbox" className="rounded border-gray-300" /></th>
                <th className="p-3">الاسم</th>
                <th className="p-3">رقم الهاتف</th>
                <th className="p-3">البلد</th>
                <th className="p-3">عدد الاستشارات</th>
                <th className="p-3">نوع الباقة</th>
                <th className="p-3">تاريخ الاشتراك</th>
                <th className="p-3">حالة الاشتراك</th>
                <th className="p-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((sub, i) => (
                <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="p-3 text-center"><input type="checkbox" className="rounded border-gray-300" /></td>
                  <td className="p-3 font-semibold text-gray-800">{sub.name}</td>
                  <td className="p-3 text-gray-600">{sub.phone}</td>
                  <td className="p-3 text-gray-600">{sub.country}</td>
                  <td className="p-3 text-gray-600">{sub.consultations}</td>
                  <td className="p-3 text-gray-600">{sub.plan}</td>
                  <td className="p-3 text-gray-600">{sub.date}</td>
                  <td className="p-3"><StatusPill active={sub.active} /></td>
                  <td className="p-3">
                    <button className="text-gray-500 hover:text-gray-700"><MoreHorizontal size={20} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 text-sm text-gray-600 border-t border-gray-200">
          <div>الصفحة 1 من 90</div>
          <div className="flex items-center gap-2" dir="ltr">
            <button className="px-2 py-1 rounded-md border border-gray-200">«</button>
            <button className="px-2.5 py-1 rounded-md border bg-gray-900 text-white border-gray-900">1</button>
            <button className="px-2.5 py-1 rounded-md border border-gray-200">2</button>
            <button className="px-2.5 py-1 rounded-md border border-gray-200">3</button>
            <span className="px-2">…</span>
            <button className="px-2.5 py-1 rounded-md border border-gray-200">90</button>
            <button className="px-2 py-1 rounded-md border border-gray-200">»</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
